import { defineStore } from "pinia";
import clone from "clone";
import collect from "collect.js";
import qs from "querystringify";
import slugify from "slugify";
import { parseAndFormatArtworkGeometry } from "@/lib/geometry.js";
import { pathTracerSupported } from "@/lib/pathtracer-support.js";
import { sortBySize } from "@/lib/sizes.js";
import { CAROUSEL_VIDEOS_BY_VARIANT_ID, MYSTERY_HAT_PRODUCT_ID, MYSTERY_TEE_PRODUCT_ID, PRESELECTED_PRODUCT_IDS, SIDEKICK_POCKET_PRODUCT_ID } from "@/lib/storefront-overrides.js";

// The product page's choices, in order, by product type: [name, title, CSS
// class, flags]. Each choice's options come from the `${name}Options` action
// below; init() expands these into `{ name, title, class, ...flags, options }`.
const COLOR_MATERIAL = ["colorMaterial", "Material & Color", "color", { showSwatch: true }];
const SIZE = ["size", "Size", "size"];
const SIZE_SHIRT = ["size", "Size", "shirt"];
const TYPE = ["type", "Type", "type"];
const FIT = ["fit", "Fit", "fit", { showKidsAdultsLink: true }];
const STYLE = ["style", "Style", "shirt"];

const COLOR_AND_SIZE = [COLOR_MATERIAL, SIZE];
const COLOR_AND_SIZE_SHIRT = [COLOR_MATERIAL, SIZE_SHIRT];
const HEADWEAR = [TYPE, FIT, STYLE, COLOR_MATERIAL, SIZE];

const CHOICE_SCHEMAS = {
  beanie: HEADWEAR,
  bottle: COLOR_AND_SIZE,
  "challenge-coin": COLOR_AND_SIZE,
  clothing: [TYPE, FIT, ["style", "Style", "shirt", { showMoreInfoLink: true }], COLOR_MATERIAL, ["size", "Size", "size", { showSizeChartLink: true }]],
  drinkware: [TYPE, STYLE, ["colorMaterial", "Material", "color", { showSwatch: true }], SIZE],
  hat: HEADWEAR,
  headphones: COLOR_AND_SIZE,
  metalcard: [["size", "Batch", "size"]],
  mug: COLOR_AND_SIZE,
  notebook: COLOR_AND_SIZE,
  patch: COLOR_AND_SIZE,
  phonecase: [
    ["generation", "Generation", "generation"],
    ["model", "Model", "model"],
    ["case", "Case", "case", { showMoreInfoLink: true }],
    ["color", "Color", "color", { showSwatch: true }],
  ],
  pin: COLOR_AND_SIZE_SHIRT,
  postcard: COLOR_AND_SIZE_SHIRT,
  poster: COLOR_AND_SIZE_SHIRT,
  sticker: COLOR_AND_SIZE_SHIRT,
  tote: COLOR_AND_SIZE_SHIRT,
  wristband: COLOR_AND_SIZE,
};
const DEFAULT_CHOICE_SCHEMA = COLOR_AND_SIZE_SHIRT;
const SUBSCRIPTION_CHOICE_SCHEMA = [COLOR_MATERIAL, ["size", "Type", "size"]];
// Mystery Tee (MYSTERY_TEE_PRODUCT_ID): clothing's choices without the kids/adults, more-info and size-chart links or swatches.
const MYSTERY_TEE_CHOICE_SCHEMA = [TYPE, ["fit", "Fit", "fit"], STYLE, ["colorMaterial", "Material & Color", "color"], SIZE];
// Sidekick Pocket (SIDEKICK_POCKET_PRODUCT_ID): one swatch per variant.
const SIDEKICK_POCKET_CHOICE_SCHEMA = [["sidekickPocket", "Type", "color", { showSwatch: true }]];

// Product-selector "choice engine" — the recursive variant picker extracted
// from storefront/product.vue. Holds the choice schema + the built option tree
// and resolves a single `selectedVariant`. Everything downstream (carousel,
// 3D, pricing, buy button) is a pure reactive derivation of `selectedVariant`
// + `activeChoices` + `userMadeFinalChoice`, read by the component via
// mapState — so moving the state here is render-neutral as long as the same
// values flow out. Blank/size-chart state stays in the component.
//
// `product` is seeded (by reference) from the component prop in `init()` and
// fully re-seeded on every mount via `$reset()` first, so the store never
// leaks the previous product across Inertia page swaps.
export const useProductSelectorStore = defineStore("productSelector", {
  state: () => ({
    product: null,
    choices: [],
    rootChoice: {},
    selectedVariant: null,
    activeChoices: [],
    selectedOptions: [],
    userMadeFinalChoice: false,
    currentVariantHashId: null,
  }),

  getters: {
    // Carousel + pricing derivations — pure functions of `selectedVariant` /
    // `activeChoices` / `product`, moved here from product.vue's computeds so
    // the choice engine is the single source of truth (the component re-exposes
    // them via mapState). Render-neutral: same logic, same inputs.
    carouselImages() {
      if (this.selectedVariant) {
        let images = [];
        // Alt text for the gallery's images: what the design looks like (altText, written by Claude
        // from the artwork) on the main mockup, and the product's name on the rest. `label` stays
        // the slide's short caption.
        const title = String(this.product.title || "").trim();
        const named = (what) => (what ? `${title}, ${what}` : title);

        if (this.product.supportsOnDemandMockups && this.selectedVariant.code !== "RF497") {
          let label = "undefined";

          if (["clothing", "hat", "beanie", "tote", "sticker"].includes(this.product.type)) {
            label = String(this.activeChoices?.find((choice) => choice.name === "colorMaterial")?.selectedOption.name);
          } else if (this.product.type === "phonecase") {
            const model = String(this.activeChoices?.find((choice) => choice.name === "model")?.selectedOption.name);
            const phonecase = String(this.activeChoices?.find((choice) => choice.name === "case")?.selectedOption.name);
            label = `${model} ${phonecase}`;
          } else if (this.product.type === "drinkware") {
            label = "Render";
          }

          // Push the primary server-rendered mockup. For drinkware this is the pre-rendered Blender still
          // ("Render") — it sits right after the interactive "3D"/"Ray Traced" model slides, since models
          // render first in the gallery (see NylonProductGallery items()) and this is the first image.
          const shownIn = label && label !== "undefined" && label !== "Render" ? label : "";
          images.push({
            props: { variantId: this.selectedVariant.id },
            label: label,
            alt: this.product.altText ? `${title}: ${this.product.altText}${shownIn ? ` Shown in ${shownIn}.` : ""}` : named(shownIn),
          });

          // Second impression: JIT renders /mockup?side=back whenever back
          // artwork exists. Variant-index images below stay as intentional
          // additional static photography.
          if (this.product.hasBackPrint && this.product.type === "clothing") {
            images.push({
              props: { variantId: this.selectedVariant.id, side: "back" },
              label: "Back",
              alt: named("back print"),
            });
          }

          this.selectedVariant.images.forEach((image, index) => {
            images.push({
              props: { variantId: this.selectedVariant.id, index: index },
              label: image.label,
              alt: named(image.label),
            });
          });

          if (!(this.product.type === "hat" || this.product.type === "beanie" || this.product.type === "sticker")) {
            images.push({
              props: { variantId: this.selectedVariant.id, detail: true },
              label: "Detail",
              alt: named("close-up of the print"),
            });
          }
        } else {
          this.selectedVariant.images.forEach((image, index) => {
            images.push({
              // The first photo is the main one: it gets the design's description too.
              props: { variantId: this.selectedVariant.id, index: index },
              label: image.label,
              alt: index === 0 && this.product.altText ? `${title}: ${this.product.altText}` : named(image.label),
            });
          });
        }

        const detailImages = this.product.images.filter((image) => image.label.search(/detail/i) >= 0);

        detailImages.forEach((image, index) => {
          images.push({
            props: { productId: this.product.id, detail: true, index: index },
            label: image.label,
          });
        });

        const posterImage = this.product.images.find((image) => image.label.search(/poster/i) >= 0);

        if (posterImage) {
          images.push({
            props: { productId: this.product.id, poster: true },
            label: posterImage.label,
          });
        }

        images = images.map((image) => {
          image.props = { ...image.props, hash: this.product.imageCacheHash };
          return image;
        });

        return images;
      } else {
        return [];
      }
    },

    carouselVideos() {
      return (this.selectedVariant && CAROUSEL_VIDEOS_BY_VARIANT_ID[this.selectedVariant.id]) || [];
    },

    carouselModels() {
      let models = [];

      if (this.selectedVariant?.model3D) {
        const model = this.selectedVariant.model3D;

        const params = {
          vid: this.selectedVariant.id,

          hash: this.product.imageCacheHash,
        };

        const query = qs.stringify(params, true);

        const shared = { src: model.model.low, artwork: `/mockup/drinkware-texture${query}`, camera: model.camera };

        // Slides driven by the native three.js <ThreeDimensionalModel>:
        //  1. "3D"         — interactive real-time raster render (spins instantly). Small shaders, compiles
        //                    in ms — offered on every platform.
        //  2. "Ray Traced" — in-browser path-traced render (converges to photoreal). WebGL tracer on Apple,
        //                    WebGPU elsewhere (the WebGL kernel's compile freezes Windows for 10s+), so it's
        //                    offered on Apple and on WebGPU browsers (see pathTracerSupported). Where it's
        //                    dropped, the pre-rendered Blender "Render" still (carouselImages) is the
        //                    photoreal fallback.
        // Both thumbnails reuse the server Blender render ({ variantId } -> ProductImageV2), which is
        // also the fallback the gallery shows if the browser can't run the renderer.
        models.push({
          props: { ...shared, mode: "raster" },
          thumbProps: { variantId: this.selectedVariant.id },
          label: "3D",
        });
        if (pathTracerSupported()) {
          models.push({
            props: { ...shared, mode: "pathtraced" },
            thumbProps: { variantId: this.selectedVariant.id },
            label: "Ray Traced",
          });
        }
      }

      models = models.map((model) => {
        model.thumbProps = { ...model.thumbProps, hash: this.product.imageCacheHash };
        return model;
      });

      return models;
    },

    carouselStaticImages() {
      const images = [];

      if (this.selectedVariant) {
        if (this.product.supportsOnDemandMockups) {
          if (this.product.type === "drinkware") {
            let path = null;

            switch (this.selectedVariant.code) {
              case "tA20sSteel":
              case "tR20sWhite":
              case "tR20sBlack":
                path = "drinkware/photography/everyday-tumbler.png";
                break;
              case "gC16":
                path = "drinkware/photography/pint-glass.png";
                break;
              case "wbV20sWhite":
                path = "drinkware/photography/skinny-bottle.png";
                break;
              case "cG16aClear":
                path = "drinkware/photography/straw-cup.png";
                break;
              case "gR10":
                path = "drinkware/photography/rocks-glass.png";
                break;
              case "tK12sWhite":
                path = "drinkware/photography/skinny-koozie.png";
                break;
              case "tB12sWhite":
                break;
            }

            if (path) {
              images.push({
                props: { path: "image", imgixParams: { path: path } },
                label: "Photo",
              });
            }
          }
        }
      }

      return images;
    },

    // Both are null until selectOption picks a variant (after $reset(), or a
    // product with no variants); with a variant they are exactly the
    // variant's values, as before.
    retailPrice() {
      if (!this.selectedVariant) {
        return null;
      }

      if (this.selectedVariant.retailPrice !== null) {
        return this.selectedVariant.retailPrice;
      } else {
        return this.selectedVariant.price;
      }
    },

    discountedPrice() {
      return this.selectedVariant ? this.selectedVariant.price : null;
    },
  },

  actions: {
    // Seeds the engine for a product and builds the initial selection. Called
    // unconditionally from the component's created() (CLAUDE.md store-seed rule).
    init(product) {
      this.$reset();
      this.product = product;
      this.currentVariantHashId = this._readVariantIdFromHash();

      let schema = DEFAULT_CHOICE_SCHEMA;

      if (this.product.subscriptions.length) {
        schema = SUBSCRIPTION_CHOICE_SCHEMA;
      } else if (this.product.id == MYSTERY_TEE_PRODUCT_ID) {
        schema = MYSTERY_TEE_CHOICE_SCHEMA;
      } else if (this.product.id == SIDEKICK_POCKET_PRODUCT_ID) {
        schema = SIDEKICK_POCKET_CHOICE_SCHEMA;
      } else if (Object.hasOwn(CHOICE_SCHEMAS, this.product.type)) {
        schema = CHOICE_SCHEMAS[this.product.type];
      }

      this.choices = schema.map(([name, title, cssClass, flags = {}]) => ({ name, title, class: cssClass, ...flags, options: [] }));

      if (this.choices.length) {
        this.rootChoice = this.buildChoice(this.product.variants, 0);
        this.activeChoices[0] = this.rootChoice;
        this.selectOption(0, this.rootChoice.selectedOption, false);
      }
    },

    buildChoice(variants, depth, keepOptionsIfNone = false) {
      const nextDepth = depth + 1;
      const choice = clone(this.choices[depth]);
      choice.options = this[`${choice.name}Options`](variants);

      if (this.product.isOnDemand && nextDepth < this.choices.length) {
        const filteredOptions = choice.options.filter((option) => option.variants.some((variant) => variant.availableForSale));

        if (keepOptionsIfNone || (depth === 0 && filteredOptions.length === 0)) {
          keepOptionsIfNone = true;
        } else {
          choice.options = filteredOptions;
        }
      }

      const routeSelectedOption = choice.options.find((option) => option.variants.some((variant) => variant.id == this.currentVariantHashId));
      const defaultSelectedOption = choice.options.find((option) => option.variants.some((variant) => variant.isDefault));
      const firstSelectedOption = choice.options[0];

      let selectedOption = null;

      if (routeSelectedOption) {
        selectedOption = routeSelectedOption;
      } else if (defaultSelectedOption) {
        selectedOption = defaultSelectedOption;
      } else {
        selectedOption = firstSelectedOption;
      }

      choice.selectedOption = selectedOption;

      if (nextDepth < this.choices.length) {
        for (let option of choice.options) {
          option.nextChoice = this.buildChoice(option.variants, nextDepth, keepOptionsIfNone);
        }
      }

      return choice;
    },

    generationOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        variants: [],
      };

      switch (this.product.type) {
        case "phonecase":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.material;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.materialReadable;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Item";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      const sortOrder = ["iPhone 18", "iPhone 17", "iPhone 16", "iPhone 15", "iPhone 14", "iPhone 13", "iPhone 12", "Galaxy S24", "Galaxy S23", "Galaxy S22"];

      options.sort((a, b) => {
        const aIndex = sortOrder.indexOf(a.name);
        const bIndex = sortOrder.indexOf(b.name);

        if (aIndex < bIndex) {
          return -1;
        } else if (aIndex > bIndex) {
          return 1;
        }

        return 0;
      });

      return options;
    },

    modelOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        variants: [],
      };

      switch (this.product.type) {
        case "phonecase":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.sex;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.sexReadable;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Item";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      const sortOrder = [
        "iPhone 18 Pro",
        "iPhone 18 Pro Max",
        "iPhone 18 Duo",
        "iPhone 17 Pro",
        "iPhone 17 Pro Max",
        "iPhone 17",
        "iPhone 17 Air",
        "iPhone 17e",
        "iPhone 16 Pro",
        "iPhone 16 Pro Max",
        "iPhone 16",
        "iPhone 16 Plus",
        "iPhone 16e",
        "iPhone 15 Pro",
        "iPhone 15 Pro Max",
        "iPhone 15",
        "iPhone 15 Plus",
        "iPhone 14 Pro",
        "iPhone 14 Pro Max",
        "iPhone 14",
        "iPhone 14 Plus",
        "iPhone 13 Pro",
        "iPhone 13 Pro Max",
        "iPhone 13",
        "iPhone 13 Mini",
        "iPhone 12 Pro",
        "iPhone 12 Pro Max",
        "iPhone 12",
        "iPhone 12 Mini",
        "Galaxy S24 Ultra",
        "Galaxy S24 Plus",
        "Galaxy S24",
        "Galaxy S23 Ultra",
        "Galaxy S23 Plus",
        "Galaxy S23",
        "Galaxy S22 Ultra",
        "Galaxy S22 Plus",
        "Galaxy S22",
      ];

      options.sort((a, b) => {
        const aIndex = sortOrder.indexOf(a.name);
        const bIndex = sortOrder.indexOf(b.name);

        if (aIndex < bIndex) {
          return -1;
        } else if (aIndex > bIndex) {
          return 1;
        }

        return 0;
      });

      return options;
    },

    caseOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        variants: [],
      };

      const metaMap = {
        Clear: {
          iconName: "phone-case-clear-detail",
          description: "UV-printed clear case.",
        },
        "Clear MagSafe": {
          iconName: "phone-case-tough-glossy-magsafe-detail",
          description: "UV-printed clear case… with magnets!",
        },
        Slim: {
          iconName: "phone-case-slim-glossy-detail",
          description: "Rich color polycarbonate shell.",
        },
        "Slim Matte": {
          iconName: "phone-case-slim-matte-detail",
          description: "Polycarbonate shell.",
        },
        Tough: {
          iconName: "phone-case-tough-glossy-detail",
          description: "Rich color polycarbonate shell + TPU liner.",
        },
        "Tough Matte": {
          iconName: "phone-case-tough-matte-detail",
          description: "Polycarbonate shell + TPU liner.",
        },
        "Tough MagSafe": {
          iconName: "phone-case-tough-glossy-magsafe-detail",
          description: "Rich color polycarbonate shell + TPU liner… with magnets!",
        },
        "Tough Matte MagSafe": {
          iconName: "phone-case-tough-matte-magsafe-detail",
          description: "Rich color polycarbonate shell + TPU liner… with magnets!",
        },
      };

      switch (this.product.type) {
        case "phonecase":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.size;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              // A case style missing from metaMap gets the generic phone-case
              // icon and no description rather than throwing.
              option.description = metaMap[firstVariant.sizeReadable]?.description ?? "";
              option.iconName = metaMap[firstVariant.sizeReadable]?.iconName ?? "phone-case-detail";
              option.name = firstVariant.sizeReadable;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Item";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      const sortOrder = ["Tough MagSafe", "Tough Matte MagSafe", "Clear MagSafe", "Tough", "Tough Matte", "Slim", "Slim Matte", "Clear"];

      options.sort((a, b) => {
        const aIndex = sortOrder.indexOf(a.name);
        const bIndex = sortOrder.indexOf(b.name);

        if (aIndex < bIndex) {
          return -1;
        } else if (aIndex > bIndex) {
          return 1;
        }

        return 0;
      });

      return options;
    },

    colorOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        colorClass: "",
        variants: [],
      };

      switch (this.product.type) {
        case "phonecase":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.colorReadable;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.colorReadable;
              option.colorClass = firstVariant.iconColor;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Item";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      return options;
    },

    typeOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        iconName: "",
        variants: [],
      };

      switch (this.product.type) {
        case "clothing":
          if (consideredVariants.find((variant) => variant.type === "tee")) {
            const option = clone(optionTemplate);
            option.name = "Tee";
            option.iconName = "type-tee";
            option.variants = consideredVariants.filter((variant) => variant.type === "tee");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "long sleeve tee")) {
            const option = clone(optionTemplate);
            option.name = "Long Sleeve Tee";
            option.iconName = "type-long-sleeve-tee";
            option.variants = consideredVariants.filter((variant) => variant.type === "long sleeve tee");
            options.push(option);
          }

          const sweatshirtTypes = ["pullover crewneck", "sweatshirt"];
          if (consideredVariants.find((variant) => sweatshirtTypes.find((type) => variant.type === type))) {
            const option = clone(optionTemplate);
            option.name = "Pullover Crewneck";
            option.iconName = "type-sweatshirt";
            option.variants = consideredVariants.filter((variant) => sweatshirtTypes.find((type) => variant.type === type));
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "pullover hoodie")) {
            const option = clone(optionTemplate);
            option.name = "Pullover Hoodie";
            option.iconName = "type-pullover-hoodie";
            option.variants = consideredVariants.filter((variant) => variant.type === "pullover hoodie");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "zip-up hoodie")) {
            const option = clone(optionTemplate);
            option.name = "Zip-up Hoodie";
            option.iconName = "type-zip-hoodie";
            option.variants = consideredVariants.filter((variant) => variant.type === "zip-up hoodie");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "pullover quarter zip")) {
            const option = clone(optionTemplate);
            option.name = "Quarter-Zip Pullover";
            option.iconName = "quarter-zip";
            option.variants = consideredVariants.filter((variant) => variant.type === "pullover quarter zip");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "tank")) {
            const option = clone(optionTemplate);
            option.name = "Tank";
            option.iconName = "type-tank";
            option.variants = consideredVariants.filter((variant) => variant.type === "tank");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "polo")) {
            const option = clone(optionTemplate);
            option.name = "Polo";
            option.iconName = "type-polo";
            option.variants = consideredVariants.filter((variant) => variant.type === "polo");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "windbreaker")) {
            const option = clone(optionTemplate);
            option.name = "Windbreaker";
            option.iconName = "type-windbreaker";
            option.variants = consideredVariants.filter((variant) => variant.type === "windbreaker");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "bomber jacket")) {
            const option = clone(optionTemplate);
            option.name = "Bomber Jacket";
            option.iconName = "type-windbreaker";
            option.variants = consideredVariants.filter((variant) => variant.type === "bomber jacket");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "onesie")) {
            const option = clone(optionTemplate);
            option.name = "Onesie";
            option.iconName = "type-onesie";
            option.variants = consideredVariants.filter((variant) => variant.type === "onesie");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "adult onesie")) {
            const option = clone(optionTemplate);
            option.name = "Adult Onesie";
            option.iconName = "type-long-sleeve-tee";
            option.variants = consideredVariants.filter((variant) => variant.type === "adult onesie");
            options.push(option);
          }

          if (consideredVariants.find((variant) => variant.type === "joggers")) {
            const option = clone(optionTemplate);
            option.name = "Joggers";
            option.iconName = "";
            option.variants = consideredVariants.filter((variant) => variant.type === "joggers");
            options.push(option);
          }
          break;
        case "hat":
          {
            const option = clone(optionTemplate);
            option.name = "Hat";
            option.iconName = "type-cap";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
        case "beanie":
          {
            const option = clone(optionTemplate);
            option.name = "Beanie";
            option.iconName = "beanie";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
        case "drinkware":
          {
            const iconLookup = function (variant) {
              switch (variant.typeReadable) {
                case "Tumbler":
                  return "type-tumbler";
                case "Water Bottle":
                  return "type-bottle";
                case "Cup":
                  return "type-cup";
                case "Glass":
                  return "type-glass";
                case "Can Sleeve":
                  return "type-koozie";
                case "Mug":
                  return "type-mug";
                default:
                  return "";
              }
            };

            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.typeReadable;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.typeReadable;
              option.iconName = iconLookup(firstVariant);
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Item";
            option.iconName = "type-generic";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      return options;
    },

    fitOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        iconName: "",
        variants: [],
      };

      switch (this.product.type) {
        case "clothing":
        case "hat":
        case "beanie":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.sex;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.sexReadable;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Fit";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      return options;
    },

    styleOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        variants: [],
      };

      switch (this.product.type) {
        case "clothing":
          options.push(...this._catalogStyleOptions(consideredVariants, optionTemplate));

          options.sort((a, b) => {
            if (a.name < b.name) {
              return 1;
            } else if (a.name > b.name) {
              return -1;
            }

            return 0;
          });

          // A blank with no style option (blanks.option_sort is null) would
          // leave this choice with no options, and buildChoice would then
          // select `undefined`. Fall back to one generic option so the page
          // still renders. There is no generic
          // detail icon in the sprite, so the figure stays empty (same as
          // Trackpants).
          if (options.length === 0 && consideredVariants.length > 0) {
            const option = clone(optionTemplate);
            option.name = consideredVariants[0].typeReadable || "Style";
            option.iconName = "";
            option.variants = consideredVariants.slice();
            options.push(option);
          }

          break;
        case "hat":
          {
            // The Mystery Hat is the only hat blank with a style option.
            const catalogOptions = this._catalogStyleOptions(consideredVariants, optionTemplate);

            if (catalogOptions.length) {
              options.push(...catalogOptions);
            } else {
              consideredVariants = collect(consideredVariants);

              const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
                return `${variant.manufacturer} ${variant.type}`;
              });

              for (let group in groupedConsideredVariants.all()) {
                const variants = groupedConsideredVariants.get(group);
                const option = clone(optionTemplate);
                const firstVariant = variants.first();
                option.name = `${firstVariant.typeReadable}`;
                option.variants = variants.all();
                options.push(option);
              }
            }
          }
          break;
        case "beanie":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return `${variant.manufacturer} ${variant.type}`;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = `${firstVariant.typeReadable}`;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        case "drinkware":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.sexReadable;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.sexReadable;
              option.iconName = firstVariant.iconTemplate;
              option.description = this.product.styleOptions?.[firstVariant.styleOptionKey]?.description ?? "";
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            const option = clone(optionTemplate);
            option.name = "Generic Style";
            option.iconName = "generic-detail";
            option.variants = consideredVariants.slice();
            options.push(option);
          }
          break;
      }

      if (options.length < 1) {
        if (consideredVariants.length > 0) {
          const variant = consideredVariants[0];
          console.log(`Unable to find any matching style options for id:${variant.id}: code:${variant.code}`);
        } else {
          console.log("Unable to find any matching style options");
        }
      }

      return options;
    },

    // One option per style option (blanks.option_*) the variants point at (the server
    // sends each variant's styleOptionKey and the product's styleOptions), in
    // the entries' sort order. Variants without an entry are left out.
    _catalogStyleOptions(consideredVariants, optionTemplate) {
      const groups = new Map();

      for (const variant of consideredVariants) {
        const key = variant.styleOptionKey;
        const meta = key == null ? null : this.product.styleOptions?.[key];

        if (meta) {
          if (!groups.has(key)) {
            groups.set(key, { meta, variants: [] });
          }
          groups.get(key).variants.push(variant);
        }
      }

      return [...groups.entries()]
        .sort(([a], [b]) => a - b)
        .map(([, { meta, variants }]) => {
          const option = clone(optionTemplate);
          option.name = meta.name;
          option.iconName = meta.iconName;
          if (meta.description !== null) {
            option.description = meta.description;
          }
          option.variants = variants;
          return option;
        });
    },

    colorMaterialOptions(consideredVariants) {
      const options = [];

      const optionTemplate = {
        name: "",
        colorClass: "",
        heathered: false,
        variants: [],
      };

      switch (this.product.type) {
        case "clothing":
          {
            if (this.product.id == MYSTERY_TEE_PRODUCT_ID) {
              const option = clone(optionTemplate);
              option.name = "It’s a mystery";
              option.colorClass = "White";
              option.heathered = false;
              option.variants = consideredVariants;
              options.push(option);
            } else {
              consideredVariants = collect(consideredVariants);

              const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
                return `${variant.colorReadable} ${variant.materialReadable}`;
              });

              for (let group in groupedConsideredVariants.all()) {
                const variants = groupedConsideredVariants.get(group);
                const option = clone(optionTemplate);
                const firstVariant = variants.first();
                option.name = group;
                option.colorClass = firstVariant.iconColor;
                option.heathered = firstVariant.iconTexture;
                option.variants = variants.all();
                options.push(option);
              }
            }
          }
          break;
        case "hat":
          {
            if (this.product.id == MYSTERY_HAT_PRODUCT_ID) {
              const option = clone(optionTemplate);
              option.name = "It’s a mystery";
              option.colorClass = "White";
              option.heathered = false;
              option.variants = consideredVariants;
              options.push(option);
            } else {
              consideredVariants = collect(consideredVariants);

              const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
                return `${variant.colorReadable} ${variant.materialReadable}`;
              });

              for (let group in groupedConsideredVariants.all()) {
                const variants = groupedConsideredVariants.get(group);
                const option = clone(optionTemplate);
                option.name = group;
                option.colorClass = variants.first().iconColor;
                option.heathered = variants.first().iconTexture;
                option.variants = variants.all();
                options.push(option);
              }
            }
          }
          break;
        case "beanie":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return `${variant.colorReadable} ${variant.materialReadable}`;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              option.name = group;
              option.colorClass = variants.first().iconColor;
              option.heathered = variants.first().iconTexture;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        case "drinkware":
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return variant.colorReadable;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              const firstVariant = variants.first();
              option.name = firstVariant.colorReadable;
              option.colorClass = firstVariant.iconColor;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
        default:
          {
            consideredVariants = collect(consideredVariants);

            const groupedConsideredVariants = consideredVariants.groupBy((variant, key) => {
              return `${variant.colorReadable} ${variant.materialReadable}`;
            });

            for (let group in groupedConsideredVariants.all()) {
              const variants = groupedConsideredVariants.get(group);
              const option = clone(optionTemplate);
              option.name = group;
              option.colorClass = variants.first().iconColor;
              option.heathered = variants.first().iconTexture;
              option.variants = variants.all();
              options.push(option);
            }
          }
          break;
      }

      return options;
    },

    sizeOptions(consideredVariants) {
      const optionTemplate = {
        name: "",
        variants: [],
      };

      const stickerGeometry = this.product.type === "sticker" && this.product.isOnDemandV2;

      const options = consideredVariants.map((variant) => {
        const option = clone(optionTemplate);
        option.name = stickerGeometry
          ? parseAndFormatArtworkGeometry(variant.sizeReadable, { width: this.product.artworkWidthPixels, height: this.product.artworkHeightPixels })
          : variant.sizeReadable;
        option.variants.push(variant);
        return option;
      });

      // Variant `position` isn't reliably size-ordered (sizes added to a blank later are
      // appended to the end, e.g. a new XS lands after 2X), so present canonical size order.
      return sortBySize(options, (option) => option.variants[0].sizeReadable);
    },

    sidekickPocketOptions(consideredVariants) {
      let options = [];

      const optionTemplate = {
        name: "",
        colorClass: "",
        heathered: false,
        variants: [],
      };

      options = consideredVariants.map((variant) => {
        const option = clone(optionTemplate);

        option.name = variant.sizeReadable;
        option.colorClass = variant.iconColor;
        option.variants.push(variant);

        return option;
      });

      return options;
    },

    selectOption(choiceIndex, option, causedByUserAction) {
      const c = this.activeChoices[choiceIndex];
      c.selectedOption = option;
      this.selectedOptions[choiceIndex] = option;

      choiceIndex++;

      if ("nextChoice" in option) {
        this.activeChoices[choiceIndex] = option.nextChoice;
        this.selectOption(choiceIndex, option.nextChoice.selectedOption, false);
      } else {
        // Remove the remaining choices
        this.activeChoices.splice(choiceIndex, this.activeChoices.length);
        // Last choice sets the variant
        this.selectedVariant = option.variants[0];
        const id = this.selectedVariant.id;
        const label = slugify(this.activeChoices.map((choice) => choice.selectedOption.name).join("-"), { lower: true });
        try {
          window.history.replaceState(window.history.state, "", `#/${id}/${label}`);
          this.currentVariantHashId = id;
        } catch (err) {
          /* no-op */
        }
        this.userMadeFinalChoice = causedByUserAction;

        // If there is only one option for this choice, it can be selected by default
        if (c.options.length <= 1) {
          this.userMadeFinalChoice = true;
        }

        if (PRESELECTED_PRODUCT_IDS.includes(Number(this.product.id))) {
          this.userMadeFinalChoice = true;
        }

        // If the product is a phone case or sticker, it can be selected by default
        if (this.product.type === "phonecase" || this.product.type === "sticker") {
          this.userMadeFinalChoice = true;
        }
      }
    },

    syncFromHash() {
      this.currentVariantHashId = this._readVariantIdFromHash();
    },

    _readVariantIdFromHash() {
      if (typeof window === "undefined") return null;
      const match = window.location.hash.match(/^#\/(\d+)/);
      return match ? Number(match[1]) : null;
    },
  },
});
